import React, { useEffect } from 'react';
import * as GrainProfileActions from '../../../redux/actions/grainProfileAction';
import { useDispatch, useSelector } from 'react-redux';
import { IStore } from '../../../redux/reducers';
import { View } from 'react-native';
import { Select, Text, Input, Checkbox } from "native-base";
import { IGrainProfileExtended } from 'src/models';
import { toTitleCase } from 'src/utils/utils';
import { textStyle } from 'src/styles/textStyle';
import { containerStyle } from 'src/styles/containerStyle';
import { selectProps } from 'src/styles/selectProps';
import {getText} from 'src/utils/i18n';
import { isElectron, shouldUseLocalProcessing } from 'src/utils/miscUtils';
import * as GrainAPI from 'src/api/grain';

export default function GrainProfileDropdown() {

    const RICE_GRAIN_ID = "18"

    const grainId = useSelector((store: IStore) => store.grainType.grainId);
    const grainProfiles = useSelector((store: IStore) => store.grainProfile.grainProfiles);
    const riceProfileSelectionEnabled = useSelector((store: IStore) => store.user.userProfile.response?.settings.rice_profile_selection_enabled) || false;
    const selectedGrainProfileMapping = useSelector((store: IStore) => store.grainProfile.selectedGrainProfileMapping);
    const preferredProcessingMode = useSelector((store: IStore) => store.settings.preferredProcessingMode);
    const isOnline = useSelector((store: IStore) => store.app.isOnline);

    const profileId = useSelector((store: IStore) => store.grainProfile.profileId);

    const dispatch = useDispatch();

    const PROCESSES = ['parboiled', 'steam', 'raw'];
    const SIZES = ['short_rice', 'medium_rice', 'long_rice', '2_by_3_broken', '3_by_4_broken', 'custom_broken'];

    const [selectedProcess, setSelectedProcess] = React.useState<string | undefined>(undefined);
    const [selectedSize, setSelectedSize] = React.useState<string | undefined>(undefined);
    const [brokenThreshold, setBrokenThreshold] = React.useState<string>('3.94');
    const [tipBrokenEnable, setTipBrokenEnable] = React.useState<boolean>(false);
    const currentGrainProfiles = React.useMemo(
        () => (grainProfiles || []).filter((profile) => profile.grainId === grainId),
        [grainProfiles, grainId]
    );
    const profileIdRef = React.useRef(profileId);
    const effectiveOnline =
        isOnline !== false
        && (typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean' || navigator.onLine !== false);
    const useLocalProcessing = shouldUseLocalProcessing(preferredProcessingMode, effectiveOnline);

    React.useEffect(() => {
        profileIdRef.current = profileId;
    }, [profileId]);

    useEffect(() => {
        if (!grainId) {
            return;
        }
        if (currentGrainProfiles.length > 0) {
            const selectedProfileStillExists = currentGrainProfiles.some((profile) => profile.profileId === profileIdRef.current);
            if (!selectedProfileStillExists) {
                dispatch(GrainProfileActions.setProfileId(currentGrainProfiles[0].profileId));
            }
            return;
        }

        let cancelled = false;

        const loadLocalProfiles = async () => {
            try {
                let nextProfiles: IGrainProfileExtended[] = [];

                if (isElectron() && (!effectiveOnline || useLocalProcessing)) {
                    const electron = typeof window === 'undefined'
                        ? undefined
                        : (window as Window & {
                            electron?: {
                                getGrainProfiles?: (args: { grainId: string }) => Promise<IGrainProfileExtended[]>;
                            };
                        }).electron;

                    if (electron?.getGrainProfiles) {
                        nextProfiles = await electron.getGrainProfiles({ grainId });
                    }
                }

                if (nextProfiles.length === 0) {
                    nextProfiles = await GrainAPI.getGrainProfiles(grainId);
                }

                if (cancelled || !Array.isArray(nextProfiles)) {
                    return;
                }

                dispatch(GrainProfileActions.setGrainProfiles(nextProfiles));

                if (nextProfiles.length === 0) {
                    return;
                }

                const selectedProfileStillExists = nextProfiles.some((profile) => profile.profileId === profileIdRef.current);
                if (!selectedProfileStillExists) {
                    dispatch(GrainProfileActions.setProfileId(nextProfiles[0].profileId));
                }
            } catch (error) {
                console.warn('Failed to load local grain profiles for dropdown:', error);
            }
        };

        loadLocalProfiles();

        return () => {
            cancelled = true;
        };
    }, [currentGrainProfiles, dispatch, effectiveOnline, grainId, useLocalProcessing]);

    useEffect(() => {
        // Initialize from Redux store if available, otherwise use first item
        if (selectedGrainProfileMapping) {
            setSelectedProcess(selectedGrainProfileMapping.process)
            setSelectedSize(selectedGrainProfileMapping.size)
        } else {
            setSelectedProcess(PROCESSES[0])
            setSelectedSize(SIZES[0])
        }
    }, [selectedGrainProfileMapping]);

    useEffect(() => {
        if (grainId !== RICE_GRAIN_ID) {
            return
        }
        // Only update if we have valid selections
        if (!selectedProcess || !selectedSize) {
            return
        }

        // Create a synthetic mapping for all size options
        const syntheticMapping = {
            process: selectedProcess,
            size: selectedSize,
            profileId: profileId || '1',
            color: ''
        };

        if (selectedGrainProfileMapping?.process !== selectedProcess ||
            selectedGrainProfileMapping?.size !== selectedSize) {
            dispatch(GrainProfileActions.setSelectedGrainProfileMapping(syntheticMapping));
        }
    }, [selectedProcess, selectedSize, grainId, selectedGrainProfileMapping, profileId, dispatch]);

    useEffect(() => {
        // Update broken parameters based on selected size
        if (grainId !== RICE_GRAIN_ID || !selectedSize) {
            return;
        }

        let brokenParam: number;
        let brokenByShape: boolean;

        switch (selectedSize) {
            case 'long_rice':
                brokenParam = 4.5;
                brokenByShape = true;
                break;
            case 'medium_rice':
                brokenParam = 3.5;
                brokenByShape = true;
                break;
            case 'short_rice':
                brokenParam = 2.5;
                brokenByShape = false;
                break;
            case '2_by_3_broken':
                brokenParam = 2.5; // Will be recalculated by backend
                brokenByShape = false;
                break;
            case '3_by_4_broken':
                brokenParam = 3.5; // Will be recalculated by backend
                brokenByShape = false;
                break;
            case 'custom_broken':
                brokenParam = parseFloat(brokenThreshold) || 3.94;
                brokenByShape = tipBrokenEnable;
                break;
            default:
                return;
        }

        dispatch(GrainProfileActions.setBrokenParam(brokenParam));
        dispatch(GrainProfileActions.setBrokenByShape(brokenByShape));
    }, [selectedSize, brokenThreshold, tipBrokenEnable, grainId, dispatch]);

    const onSelectedValueChanged = (value: string) => {
        dispatch(GrainProfileActions.setProfileId(value));
    };

    const beautifyLabel = (val: string) => {
        val = val.replace('_', " ")
        return toTitleCase(val)
    }

    return (
        <View style={containerStyle.componentContainer}>
            {grainId === RICE_GRAIN_ID && <>
                <Text style={textStyle.subSectionText}>{getText('selectRiceProcess')}</Text>
                <Select
                    selectedValue={selectedProcess}
                    minWidth="200"
                    accessibilityLabel={getText('chooseRiceProcess')}
                    placeholder={getText('chooseRiceProcess')} _selectedItem={selectProps.selectedItemProps} mt={1} onValueChange={itemValue => setSelectedProcess(itemValue)}>
                    {
                        PROCESSES.map((process) => {
                            return <Select.Item key={process} label={beautifyLabel(process)} value={process} />
                        })
                    }
                </Select>
                <Text style={textStyle.subSectionText}>{getText('selectRiceSize')}</Text>
                <Select
                    selectedValue={selectedSize}
                    minWidth="200"
                    accessibilityLabel={getText('chooseRiceSize')}
                    placeholder={getText('chooseRiceSize')} _selectedItem={selectProps.selectedItemProps} mt={1} onValueChange={itemValue => setSelectedSize(itemValue)}>
                    {
                        SIZES.map((size) => {
                            return <Select.Item key={size} label={beautifyLabel(size)} value={size} />
                        })
                    }
                </Select>
                {selectedSize === 'custom_broken' && <>
                    <Text style={textStyle.subSectionText}>Broken Threshold (mm)</Text>
                    <Input
                        value={brokenThreshold}
                        keyboardType="decimal-pad"
                        placeholder={getText('enterValue')}
                        onChangeText={(value) => {
                            // Validate input: only numbers and decimal point, range 0-10
                            const numValue = parseFloat(value);
                            if (value === '' || (!isNaN(numValue) && numValue >= 0 && numValue <= 10)) {
                                setBrokenThreshold(value);
                            }
                        }}
                        mt={1}
                    />
                    <Checkbox
                        value="tipBrokenEnable"
                        isChecked={tipBrokenEnable}
                        onChange={setTipBrokenEnable}
                        mt={2}
                    >
                        <Text style={textStyle.subSectionText}>Tip Broken Enable</Text>
                    </Checkbox>
                </>}
            </>}
            {
                (riceProfileSelectionEnabled ? (grainId !== RICE_GRAIN_ID) : true)
                &&
                <>
                    <Text style={textStyle.subSectionText}>{getText('selectGrainProfile')}</Text>
                    <Select selectedValue={profileId}
                        minWidth="200"
                        accessibilityLabel={getText('chooseGrainProfile')}
                        placeholder={getText('chooseGrainProfile')}
                        _selectedItem={selectProps.selectedItemProps}
                        mt={1}
                        onValueChange={itemValue => onSelectedValueChanged(itemValue)}>
                        {currentGrainProfiles.length > 0 && currentGrainProfiles.map((profile) => {
                            return <Select.Item key={profile.profileId} label={profile.profileName} value={profile.profileId} />
                        })}
                    </Select>
                </>}
        </View>
    );
}
